import { RuleId } from '@wix/a11y-rules';
import { useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { isOptional } from '@wix/seo-common-core';
import { useAtom, useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { inaccessibleLayoutSessionAtom, inaccessibleLayoutSessionPagesAtom } from '../../atoms/session-atoms';
import { BiCtaNames } from '../../bi/types';
import { InaccessibleLayoutRuleFixer } from '../../components/fixers/inaccessible-layout-fixer/inaccessible-layout-rule-fixer';
import { getCurrentLayoutName } from '../../components/fixers/inaccessible-layout-fixer/utils/layout-utils';
import { RuleDescriptionComponent } from '../../components/rule-description-component';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useNavigation } from '../../hooks/use-navigation';
import { useOpenArticleByEditorHost } from '../../hooks/use-open-article-by-editor-host';
import { getPageTypeForBi } from '../shared/bi-helpers';
import { shouldUpdateRuleStatus } from '../shared/helpers';
export const useInaccessibleLayoutRule = () => {
  const {
    t
  } = useTranslation();
  const navigateToPage = useNavigation();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const {
    editorHost
  } = useA11yWizardAdapter();
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const inaccessibleLayoutPages = useAtomValue(inaccessibleLayoutSessionPagesAtom);
  const {
    getGroupData,
    getRuleState,
    setRuleState
  } = usePanelStateAtom();
  const sessionComponents = useAtomValue(inaccessibleLayoutSessionAtom);
  const getIsNotApplicable = useCallback(ruleExecutionResults => {
    return ruleExecutionResults.every(_ref => {
      var _result$failedElement;
      let {
        result
      } = _ref;
      return result == null || !((_result$failedElement = result.failedElements) != null && _result$failedElement.length);
    });
  }, []);
  const getInaccessibleLayout = useCallback(_ref2 => {
    let {
      ruleExecutionResult,
      pageId,
      routerId,
      isCrossPage
    } = _ref2;
    if (isOptional(pageId)) {
      throw new Error('pageId is required');
    }
    const elements = sessionComponents[pageId] ?? [];
    const notApplicable = getIsNotApplicable(ruleExecutionResult);
    if (notApplicable && elements.length === 0 && !inaccessibleLayoutPages.includes(pageId)) {
      return null;
    }
    const ruleState = getRuleState(pageId, RuleId.INACCESSIBLE_LAYOUT);
    const {
      analysisState,
      itemType
    } = getGroupData(pageId ?? '') ?? {};
    if (isOptional(itemType)) {
      throw new Error('itemType is required');
    }
    const failedElements = [];
    const uniqueElementsMap = new Map();
    ruleExecutionResult.forEach(execResult => {
      const {
        failedElements: failed
      } = execResult.result;
      failed == null || failed.forEach(failedElement => {
        const elementId = failedElement.id;
        if (!uniqueElementsMap.has(elementId)) {
          const element = {
            id: failedElement.id,
            isFixed: false,
            label: getCurrentLayoutName(failedElement.widgetId, failedElement.layoutValue),
            layoutValue: failedElement.layoutValue,
            widgetId: failedElement.widgetId
          };
          uniqueElementsMap.set(elementId, element);
          failedElements.push(element);
        }
      });
    });
    const allFailedElements = Array.from(uniqueElementsMap.values());
    const uiStatus = allFailedElements.length > 0 ? RuleListStatus.failed : RuleListStatus.passed;
    const isRuleResolved = uiStatus !== RuleListStatus.failed;
    if (shouldUpdateRuleStatus(analysisState, uiStatus, ruleState, routerId)) {
      setRuleState(pageId, RuleId.INACCESSIBLE_LAYOUT, {
        status: uiStatus
      });
    }
    if (elements.length === 0 && allFailedElements.length === 0 && !inaccessibleLayoutPages.includes(pageId)) {
      return null;
    }
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: isRuleResolved ? 'accessibility-wizard.rules-list.inaccessible-layout.description' : 'accessibility-wizard.rules-list.inaccessible-layout.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-stores-inaccessible-layout-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: elements.length,
            failedElementCount: allFailedElements.length
          });
        },
        title: isRuleResolved ? t('accessibility-wizard.rules-list.issue-resolved') : ''
      }),
      elementCount: elements.length,
      failedElementCount: failedElements.length,
      fixer: FixerType.Custom,
      fixerProps: {
        customFixer: /*#__PURE__*/React.createElement(InaccessibleLayoutRuleFixer, {
          elements: elements,
          failedElements: allFailedElements,
          pageId: pageId
        })
      },
      id: `${routerId ?? pageId}-${RuleId.INACCESSIBLE_LAYOUT}`,
      label: t('accessibility-wizard.rules-list.inaccessible-layout.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: RuleId.INACCESSIBLE_LAYOUT
        }));
        biLogger.taskExpandCollapse({
          elementCount: elements.length,
          failedElementCount: failedElements.length,
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            page_type: getPageTypeForBi(itemType, isCrossPage),
            status: uiStatus,
            task_index: ruleIndex,
            task_name: RuleId.INACCESSIBLE_LAYOUT
          },
          toggleType
        });
        if (toggleType === ToggleType.Expand) {
          navigateToPage(pageId, routerId);
        }
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: elements.length,
          failedElementCount: failedElements.length,
          status: uiStatus
        });
      },
      parentGroupId: pageId,
      ruleId: RuleId.INACCESSIBLE_LAYOUT,
      status: uiStatus
    };
  }, [getRuleState, getGroupData, openSelectedArticle, sessionComponents, t, editorHost, setRuleState, biLogger, setCurrentTaskInfo, currentTaskInfo, navigateToPage, getIsNotApplicable, inaccessibleLayoutPages]);
  return {
    getInaccessibleLayout
  };
};
//# sourceMappingURL=use-inaccessible-layout-rule.js.map